import { useState, useEffect } from "react";
import {
  X, ChevronDown, RotateCcw, SlidersHorizontal,
  FileText, Users, Package as PackageIcon, Plane, Wallet,
  ArrowLeftRight, Building2, FileCheck, Settings2,
} from "lucide-react";
import styles from "./BookingFilterDrawer.module.css";
import {
  DEFAULT_FILTERS,
  BOOKING_STATUS_OPTIONS,
  ORIGIN_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_LABEL,
  PRODUCT_STATUS_OPTIONS,
  CUSTOMER_TYPE_OPTIONS,
  SALE_TYPE_OPTIONS,
  activeFilterCount,
} from "./bookingFilters";

/* ── Reusable controls ─────────────────────────────────────────── */
const Field = ({ label, children, hint }) => (
  <div className={styles.field}>
    {label && <label className={styles.fieldLabel}>{label}</label>}
    {children}
    {hint && <span className={styles.fieldHint}>{hint}</span>}
  </div>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    className={styles.input}
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
  />
);

const ChipMulti = ({ options, value, onChange, getKey = (o) => o, getLabel = (o) => o }) => (
  <div className={styles.chips}>
    {options.map((o) => {
      const key = getKey(o);
      const active = value.includes(key);
      return (
        <button
          key={key}
          type="button"
          className={`${styles.chip} ${active ? styles.chipActive : ""}`}
          onClick={() => onChange(active ? value.filter((v) => v !== key) : [...value, key])}
        >
          {getLabel(o)}
        </button>
      );
    })}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <button type="button" className={styles.toggleRow} onClick={() => onChange(!checked)}>
    <span className={styles.toggleLabel}>{label}</span>
    <span className={`${styles.toggle} ${checked ? styles.toggleOn : ""}`}>
      <span className={styles.toggleKnob} />
    </span>
  </button>
);

const TriSelect = ({ value, onChange }) => (
  <div className={styles.segmented}>
    {[
      { v: "", l: "Any" },
      { v: "yes", l: "Yes" },
      { v: "no", l: "No" },
    ].map((o) => (
      <button
        key={o.v || "any"}
        type="button"
        className={`${styles.segment} ${value === o.v ? styles.segmentActive : ""}`}
        onClick={() => onChange(o.v)}
      >
        {o.l}
      </button>
    ))}
  </div>
);

const RangeInputs = ({ from, to, onFrom, onTo, prefix }) => (
  <div className={styles.rangeRow}>
    <div className={styles.rangeInputWrap}>
      {prefix && <span className={styles.rangePrefix}>{prefix}</span>}
      <input type="text" className={styles.rangeInput} placeholder="Min" value={from} onChange={(e) => onFrom(e.target.value)} />
    </div>
    <span className={styles.rangeDash}>–</span>
    <div className={styles.rangeInputWrap}>
      {prefix && <span className={styles.rangePrefix}>{prefix}</span>}
      <input type="text" className={styles.rangeInput} placeholder="Max" value={to} onChange={(e) => onTo(e.target.value)} />
    </div>
  </div>
);

const DateRange = ({ from, to, onFrom, onTo }) => (
  <div className={styles.rangeRow}>
    <input type="date" className={styles.dateInput} value={from} onChange={(e) => onFrom(e.target.value)} />
    <span className={styles.rangeDash}>–</span>
    <input type="date" className={styles.dateInput} value={to} onChange={(e) => onTo(e.target.value)} />
  </div>
);

const Section = ({ id, icon: Icon, title, count, openId, setOpenId, children }) => {
  const open = openId === id;
  return (
    <div className={`${styles.section} ${open ? styles.sectionOpen : ""}`}>
      <button type="button" className={styles.sectionHead} onClick={() => setOpenId(open ? null : id)}>
        <span className={styles.sectionIcon}><Icon size={15} /></span>
        <span className={styles.sectionTitle}>{title}</span>
        {count > 0 && <span className={styles.sectionCount}>{count}</span>}
        <ChevronDown size={16} className={`${styles.sectionChevron} ${open ? styles.sectionChevronOpen : ""}`} />
      </button>
      {open && <div className={styles.sectionBody}>{children}</div>}
    </div>
  );
};

/* ── Drawer ────────────────────────────────────────────────────── */
const BookingFilterDrawer = ({ open, onClose, value, onApply }) => {
  const [draft, setDraft] = useState(value || DEFAULT_FILTERS);
  const [openId, setOpenId] = useState("booking");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(value || DEFAULT_FILTERS);
      // next frame → trigger slide-in transition
      const t = setTimeout(() => setMounted(true), 10);
      return () => clearTimeout(t);
    }
    setMounted(false);
  }, [open, value]);

  if (!open) return null;

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));
  const count = activeFilterCount(draft);

  // per-section active counts (for the little badges on each accordion head)
  const c = (keys) => keys.reduce((n, k) => {
    const v = draft[k];
    if (Array.isArray(v)) return n + (v.length ? 1 : 0);
    if (typeof v === "boolean") return n + (v ? 1 : 0);
    return n + (v != null && String(v).trim() !== "" ? 1 : 0);
  }, 0);

  const handleReset = () => setDraft(DEFAULT_FILTERS);
  const handleApply = () => { onApply(draft); onClose(); };

  return (
    <div className={`${styles.overlay} ${mounted ? styles.overlayOpen : ""}`} onClick={onClose}>
      <aside
        className={`${styles.drawer} ${mounted ? styles.drawerOpen : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <span className={styles.headerIcon}><SlidersHorizontal size={17} /></span>
            <div>
              <h3>Filters</h3>
              <p>{count > 0 ? `${count} active filter${count > 1 ? "s" : ""}` : "Refine the booking list"}</p>
            </div>
          </div>
          <button className={styles.headerClose} onClick={onClose}><X size={18} /></button>
        </div>

        {/* Sections */}
        <div className={styles.body}>
          <Section id="booking" icon={FileText} title="Booking" openId={openId} setOpenId={setOpenId}
            count={c(["bookingStatus", "bookingReference", "invoiceNumber", "saleType", "origins", "createdFrom", "createdTo", "createdBy", "locked"])}>
            <Field label="Booking Reference"><TextInput value={draft.bookingReference} onChange={(v) => set("bookingReference", v)} placeholder="ORD-000123" /></Field>
            <Field label="Invoice Number" hint="Searched across generated invoices"><TextInput value={draft.invoiceNumber} onChange={(v) => set("invoiceNumber", v)} placeholder="INV-…" /></Field>
            <Field label="Booking Status"><ChipMulti options={BOOKING_STATUS_OPTIONS} value={draft.bookingStatus} onChange={(v) => set("bookingStatus", v)} /></Field>
            <Field label="Origin"><ChipMulti options={ORIGIN_OPTIONS} value={draft.origins} onChange={(v) => set("origins", v)} /></Field>
            <Field label="Sale Type">
              <ChipMulti options={SALE_TYPE_OPTIONS} value={draft.saleType ? [draft.saleType] : []}
                getKey={(o) => o.key} getLabel={(o) => o.label}
                onChange={(v) => set("saleType", v[v.length - 1] || "")} />
            </Field>
            <Field label="Created Date"><DateRange from={draft.createdFrom} to={draft.createdTo} onFrom={(v) => set("createdFrom", v)} onTo={(v) => set("createdTo", v)} /></Field>
            <Field label="Created By"><TextInput value={draft.createdBy} onChange={(v) => set("createdBy", v)} placeholder="User id / name" /></Field>
            <Field label="Locked"><TriSelect value={draft.locked} onChange={(v) => set("locked", v)} /></Field>
          </Section>

          <Section id="customer" icon={Users} title="Customer & Traveler" openId={openId} setOpenId={setOpenId}
            count={c(["custFirstName", "custLastName", "custEmail", "customerType", "travFirstName", "travLastName", "travelerType"])}>
            <div className={styles.subLabel}>Customer</div>
            <div className={styles.grid2}>
              <Field label="First Name"><TextInput value={draft.custFirstName} onChange={(v) => set("custFirstName", v)} /></Field>
              <Field label="Last Name"><TextInput value={draft.custLastName} onChange={(v) => set("custLastName", v)} /></Field>
            </div>
            <Field label="Email"><TextInput value={draft.custEmail} onChange={(v) => set("custEmail", v)} placeholder="name@email.com" /></Field>
            <Field label="Customer Type">
              <ChipMulti options={CUSTOMER_TYPE_OPTIONS} value={draft.customerType ? [draft.customerType] : []}
                onChange={(v) => set("customerType", v[v.length - 1] || "")} />
            </Field>
            <div className={styles.subLabel}>Traveler <span className={styles.subLabelNote}>· via global search</span></div>
            <div className={styles.grid2}>
              <Field label="First Name"><TextInput value={draft.travFirstName} onChange={(v) => set("travFirstName", v)} /></Field>
              <Field label="Last Name"><TextInput value={draft.travLastName} onChange={(v) => set("travLastName", v)} /></Field>
            </div>
            <Field label="Traveler Type">
              <ChipMulti options={["ADT", "CHD", "INF"]} value={draft.travelerType ? [draft.travelerType] : []}
                onChange={(v) => set("travelerType", v[v.length - 1] || "")} />
            </Field>
          </Section>

          <Section id="products" icon={PackageIcon} title="Products" openId={openId} setOpenId={setOpenId}
            count={c(["hasFlight", "hasHotel", "hasTransfer", "hasPackage", "hasInsurance", "hasCarRental", "hasVisa", "hasCruise", "productStatus"])}>
            <div className={styles.subLabel}>Product presence</div>
            <Toggle label="Has Flight" checked={draft.hasFlight} onChange={(v) => set("hasFlight", v)} />
            <Toggle label="Has Accommodation" checked={draft.hasHotel} onChange={(v) => set("hasHotel", v)} />
            <Toggle label="Has Transfer" checked={draft.hasTransfer} onChange={(v) => set("hasTransfer", v)} />
            <Toggle label="Has Package Holiday" checked={draft.hasPackage} onChange={(v) => set("hasPackage", v)} />
            <Toggle label="Has Insurance" checked={draft.hasInsurance} onChange={(v) => set("hasInsurance", v)} />
            <Toggle label="Has Car Rental" checked={draft.hasCarRental} onChange={(v) => set("hasCarRental", v)} />
            <Toggle label="Has Visa" checked={draft.hasVisa} onChange={(v) => set("hasVisa", v)} />
            <Toggle label="Has Cruise" checked={draft.hasCruise} onChange={(v) => set("hasCruise", v)} />
            <Field label="Product Status"><ChipMulti options={PRODUCT_STATUS_OPTIONS} value={draft.productStatus} onChange={(v) => set("productStatus", v)} /></Field>
          </Section>

          <Section id="details" icon={Plane} title="Product Details" openId={openId} setOpenId={setOpenId}
            count={c(["flightAirline", "flightNumber", "flightPnr", "flightDepAirport", "flightArrAirport", "hotelName", "hotelCity", "hotelCountry", "transferPickup", "transferDropoff", "visaCountry", "visaType", "cruiseLine", "cruiseCabin"])}>
            <div className={styles.subLabel}>Flight</div>
            <div className={styles.grid2}>
              <Field label="Airline"><TextInput value={draft.flightAirline} onChange={(v) => set("flightAirline", v)} /></Field>
              <Field label="Flight Number"><TextInput value={draft.flightNumber} onChange={(v) => set("flightNumber", v)} /></Field>
            </div>
            <div className={styles.grid2}>
              <Field label="Departure"><TextInput value={draft.flightDepAirport} onChange={(v) => set("flightDepAirport", v)} placeholder="BRU" /></Field>
              <Field label="Arrival"><TextInput value={draft.flightArrAirport} onChange={(v) => set("flightArrAirport", v)} placeholder="AYT" /></Field>
            </div>
            <Field label="Airline PNR"><TextInput value={draft.flightPnr} onChange={(v) => set("flightPnr", v)} /></Field>
            <div className={styles.subLabel}>Accommodation</div>
            <Field label="Hotel Name"><TextInput value={draft.hotelName} onChange={(v) => set("hotelName", v)} /></Field>
            <div className={styles.grid2}>
              <Field label="City"><TextInput value={draft.hotelCity} onChange={(v) => set("hotelCity", v)} /></Field>
              <Field label="Country"><TextInput value={draft.hotelCountry} onChange={(v) => set("hotelCountry", v)} /></Field>
            </div>
            <div className={styles.subLabel}>Transfer</div>
            <div className={styles.grid2}>
              <Field label="Pickup"><TextInput value={draft.transferPickup} onChange={(v) => set("transferPickup", v)} /></Field>
              <Field label="Drop-off"><TextInput value={draft.transferDropoff} onChange={(v) => set("transferDropoff", v)} /></Field>
            </div>
            <div className={styles.subLabel}>Visa & Cruise</div>
            <div className={styles.grid2}>
              <Field label="Visa Country"><TextInput value={draft.visaCountry} onChange={(v) => set("visaCountry", v)} /></Field>
              <Field label="Visa Type"><TextInput value={draft.visaType} onChange={(v) => set("visaType", v)} /></Field>
            </div>
            <div className={styles.grid2}>
              <Field label="Cruise Line"><TextInput value={draft.cruiseLine} onChange={(v) => set("cruiseLine", v)} /></Field>
              <Field label="Cabin Type"><TextInput value={draft.cruiseCabin} onChange={(v) => set("cruiseCabin", v)} /></Field>
            </div>
          </Section>

          <Section id="financial" icon={Wallet} title="Financial" openId={openId} setOpenId={setOpenId}
            count={c(["totalMin", "totalMax", "outstandingMin", "outstandingMax", "marginMin", "marginMax", "paymentStatuses", "hasCredit"])}>
            <Field label="Total Selling Price"><RangeInputs prefix="€" from={draft.totalMin} to={draft.totalMax} onFrom={(v) => set("totalMin", v)} onTo={(v) => set("totalMax", v)} /></Field>
            <Field label="Outstanding Amount"><RangeInputs prefix="€" from={draft.outstandingMin} to={draft.outstandingMax} onFrom={(v) => set("outstandingMin", v)} onTo={(v) => set("outstandingMax", v)} /></Field>
            <Field label="Margin"><RangeInputs prefix="€" from={draft.marginMin} to={draft.marginMax} onFrom={(v) => set("marginMin", v)} onTo={(v) => set("marginMax", v)} /></Field>
            <Field label="Payment Status">
              <ChipMulti options={PAYMENT_STATUS_OPTIONS} value={draft.paymentStatuses} onChange={(v) => set("paymentStatuses", v)} getLabel={(o) => PAYMENT_STATUS_LABEL[o]} />
            </Field>
            <Toggle label="Has Credit" checked={draft.hasCredit} onChange={(v) => set("hasCredit", v)} />
          </Section>

          <Section id="payments" icon={ArrowLeftRight} title="Payments & Refunds" openId={openId} setOpenId={setOpenId}
            count={c(["hasIncomingPayment", "hasSupplierPayment", "hasClientRefund", "hasSupplierRefund", "refundPending"])}>
            <Toggle label="Has Incoming Payment" checked={draft.hasIncomingPayment} onChange={(v) => set("hasIncomingPayment", v)} />
            <Toggle label="Has Supplier Payment" checked={draft.hasSupplierPayment} onChange={(v) => set("hasSupplierPayment", v)} />
            <Toggle label="Has Client Refund" checked={draft.hasClientRefund} onChange={(v) => set("hasClientRefund", v)} />
            <Toggle label="Has Supplier Refund" checked={draft.hasSupplierRefund} onChange={(v) => set("hasSupplierRefund", v)} />
            <Toggle label="Refund Pending" checked={draft.refundPending} onChange={(v) => set("refundPending", v)} />
          </Section>

          <Section id="suppliers" icon={Building2} title="Suppliers" openId={openId} setOpenId={setOpenId}
            count={c(["supplierName", "supplierBookingRef"])}>
            <Field label="Supplier Name"><TextInput value={draft.supplierName} onChange={(v) => set("supplierName", v)} /></Field>
            <Field label="Supplier Booking Reference"><TextInput value={draft.supplierBookingRef} onChange={(v) => set("supplierBookingRef", v)} /></Field>
          </Section>

          <Section id="documents" icon={FileCheck} title="Documents & Status" openId={openId} setOpenId={setOpenId}
            count={c(["invoiceGenerated", "documentsSent", "bookingLocked"])}>
            <Field label="Invoice Generated"><TriSelect value={draft.invoiceGenerated} onChange={(v) => set("invoiceGenerated", v)} /></Field>
            <Field label="Documents Sent"><TriSelect value={draft.documentsSent} onChange={(v) => set("documentsSent", v)} /></Field>
            <Field label="Booking Locked"><TriSelect value={draft.bookingLocked} onChange={(v) => set("bookingLocked", v)} /></Field>
          </Section>

          <Section id="advanced" icon={Settings2} title="Advanced" openId={openId} setOpenId={setOpenId}
            count={c(["createdBy", "hasNotes"])}>
            <Field label="Created By"><TextInput value={draft.createdBy} onChange={(v) => set("createdBy", v)} placeholder="User id / name" /></Field>
            <Toggle label="Has Notes" checked={draft.hasNotes} onChange={(v) => set("hasNotes", v)} />
          </Section>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.resetBtn} onClick={handleReset}>
            <RotateCcw size={14} /> Reset
          </button>
          <button className={styles.applyBtn} onClick={handleApply}>
            Apply{count > 0 ? ` (${count})` : ""}
          </button>
        </div>
      </aside>
    </div>
  );
};

export default BookingFilterDrawer;
